import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/site/PageShell";
import { ServicePage } from "@/components/site/ServicePage";
import heroImg from "@/assets/portfolio-5.jpg";
import heroImgWebp from "@/assets/portfolio-5.webp";
import { seoMeta, canonicalLink, robotsMeta } from "@/lib/seo";
import { contenido } from "@/content/pages/canastas-navidenas-corporativas";
import { vistaServicio } from "@/lib/page-content/vista";
import type { ServiceSchemaData } from "@/lib/seo-schema-apply";
import { datosHeadServicio, jsonLdServicio } from "@/lib/jsonld-servicios";

const DEFAULT_TITLE = "Canastas Navideñas Corporativas en Lima y Perú | Improve";
const DEFAULT_DESCRIPTION =
  "Canastas navideñas y packs de fin de año para empresas, personalizados con tu marca. Opciones según tu presupuesto y envío a todo el Perú. Cotiza ya.";

// Imagen provisional (del sitio) hasta asignar una propia al slot «servicio-canastas-navidenas-corporativas» desde el panel.
const props = {
  slug: "/canastas-navidenas-corporativas" as const,
  // Landing de temporada (no está en SERVICES): formulario, WhatsApp y CRM van como merchandising.
  servicio: "/merchandising-corporativo" as const,
  heroImg,
  heroImgWebp,
  heroSrcset:
    "/assets/portfolio-5-400w.webp 400w, /assets/portfolio-5-680w.webp 680w, /assets/portfolio-5-800w.webp 800w",
};
const relatedSlugs = [
  "/merchandising-corporativo",
  "/eventos-corporativos",
  "/activaciones-btl",
] as const;

function CanastasNavidenasCorporativasPage() {
  const { siteImageSlots, pageContent } = Route.useRouteContext();
  const vista = vistaServicio(contenido, pageContent);
  const slot = siteImageSlots?.["servicio-canastas-navidenas-corporativas"];
  return (
    <PageShell>
      <ServicePage
        {...props}
        vista={vista}
        heroImg={slot?.src ?? props.heroImg}
        heroImgWebp={slot?.src ?? props.heroImgWebp}
        heroSrcset={slot?.srcSet ?? props.heroSrcset}
        relatedSlugs={[...relatedSlugs]}
      />
    </PageShell>
  );
}

export const Route = createFileRoute("/canastas-navidenas-corporativas")({
  component: CanastasNavidenasCorporativasPage,
  // El head() solo lee lo que arma el loader: el loader va en el chunk de la ruta junto con el
  // contenido, y así el contenido no entra al chunk de entrada de todas las páginas.
  loader: ({ context }) => datosHeadServicio(vistaServicio(contenido, context.pageContent)),
  head: ({ match, loaderData }) => {
    const override = match.context.seoOverride;
    const slot = match.context.siteImageSlots?.["servicio-canastas-navidenas-corporativas"];
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/canastas-navidenas-corporativas",
          image: override?.ogImage ?? slot?.src ?? heroImg,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: [
        ...canonicalLink(override?.canonicalPath ?? "/canastas-navidenas-corporativas"),
        {
          rel: "preload",
          as: "image",
          href: slot?.src ?? heroImgWebp,
          type: "image/webp",
          fetchPriority: "high",
        },
      ],
      scripts: loaderData
        ? jsonLdServicio(
            loaderData,
            "/canastas-navidenas-corporativas",
            slot?.src ?? heroImg,
            override?.schemaType === "service"
              ? (override.schemaData as Partial<ServiceSchemaData>)
              : null,
          )
        : [],
    };
  },
});
